import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { Button, Input } from '@/components/ui';
import { paths, QueryParams, REDIRECT_TO_KEY } from '@/config/paths';
import { useAuth, UserRegistrationDetailsResponse } from '@/features/auth';
import { FormFooter } from '@/features/auth/components/form-footer';
import { FormHeader } from '@/features/auth/components/form-header';
import { useNotifications } from '@/stores/notifications';
import { cn, noop } from '@/utils';

import { GoogleSignUpButton } from '../google-buttons';

const registrationSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      ),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegistrationFormData = z.infer<typeof registrationSchema>;

type RegistrationFormProps = Readonly<{
  onSubmit?: (data: RegistrationFormData) => void;
}>;

export default function RegistrationForm({ onSubmit }: RegistrationFormProps) {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const redirectTo = searchParams.get(REDIRECT_TO_KEY);
  const {
    getRegistrationDetails,
    register: registerUser,
    isRegistering,
  } = useAuth();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
  });

  // When the token's email is flagged as unverified, we discard it and let the
  // user enter their own email instead of pre-filling and locking the field.
  const [isEmailUnverified, setIsEmailUnverified] = useState(false);
  // Lead/profile the registration details belong to. Forwarded to register so
  // the backend attaches the (possibly user-typed) email to the right lead.
  const [profileId, setProfileId] = useState<string>();

  useEffect(() => {
    getRegistrationDetails(token, {
      onSuccess: data => {
        setProfileId(data.profile_id);
        // Discard the token's email and let the user type their own when the
        // backend flags it as unverified.
        if (data.unverified_email) {
          setIsEmailUnverified(true);
          return;
        }
        setValue('email', data.registration_email);
      },
      onSettled: (_, error) => {
        if (!error) return;
        const errorMessage = (
          error as AxiosError<UserRegistrationDetailsResponse.Root>
        )?.response?.data?.message;
        if (errorMessage === 'registration-done') {
          addNotification({
            title: 'Registration Done!',
            message: 'Please proceed to login',
            type: 'success',
          });
          navigate(paths.auth.login.getHref())?.catch(() => noop);
        } else if (errorMessage === 'link-expired') {
          addNotification({
            title: 'Registration Link Expired!',
            message: 'Please request for a new registration link',
            type: 'warning',
          });
        } else {
          addNotification({
            title: 'Oops!',
            message: 'Something went wrong, please try again',
            type: 'error',
          });
        }
      },
    });
  }, [getRegistrationDetails, setValue, token, navigate, addNotification]);

  const handleFormSubmit = (data: RegistrationFormData) => {
    const payload = {
      ...data,
      source: 'student_dashboard',
      ...(profileId ? { profile_id: profileId } : {}),
    };
    registerUser(payload, {
      onSuccess: () => {
        const destination = redirectTo ?? paths.app.root.getHref();
        navigate(destination)?.catch(() => noop);
      },
    });
    onSubmit?.(payload);
  };

  const onBackToLogin = async () => {
    await navigate(paths.auth.login.getHref());
  };

  const getGoogleReturnDeeplink = () => {
    const queryParams: QueryParams = {};
    if (token) {
      queryParams['token'] = token;
    }
    return paths.app.profiles.getHref({ queryParams });
  };

  const loading = isSubmitting || isRegistering;

  // The email is pre-filled and read-only when it comes from the token, except
  // when that email is unverified — then the user enters it themselves.
  const lockEmailFromToken = Boolean(token) && !isEmailUnverified;

  // Google sign-up is offered only in the verified-token flow; the
  // unverified-email flow forces email/password registration.
  const showGoogleSignUp = !isEmailUnverified;

  return (
    <div className="w-full max-w-md mx-auto p-8 bg-blue-chaos/10 rounded-4xl">
      {/* Header */}
      <FormHeader title="Registration" />

      {/* Registration Form */}
      <form
        onSubmit={event => {
          event.preventDefault();
          void handleSubmit(handleFormSubmit)(event);
        }}
        className="space-y-6"
      >
        {/* Email Field */}
        <div>
          <Input
            label="Email ID"
            type="email"
            errorText={errors.email?.message}
            disabled={lockEmailFromToken}
            forceFloat={lockEmailFromToken}
            {...register('email')}
          />
        </div>

        {/* Password Field */}
        <div>
          <Input
            label="Create Password"
            type="password"
            errorText={errors.password?.message}
            {...register('password')}
          />
        </div>

        {/* Confirm Password Field */}
        <div>
          <Input
            label="Confirm Password"
            type="password"
            errorText={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <Button
            type="submit"
            variant="raised"
            size="lg"
            className="w-56"
            disabled={loading}
          >
            {loading ? 'Signing Up...' : 'Sign Up'}
          </Button>
        </div>
      </form>

      {/* Divider — only shown alongside the Google sign-up below. */}
      {showGoogleSignUp ? (
        <div className="flex items-center my-6 flex-row">
          <div className="flex-1 border-t border-magical-moonlight-d1 border-dashed"></div>
          <span className="px-4 text-muted-foreground text-sm">or</span>
          <div className="flex-1 border-t border-magical-moonlight-d1 border-dashed"></div>
        </div>
      ) : null}

      {/* Alternative Sign-up Options */}
      <div
        className={cn(
          'space-y-3 flex-col flex',
          // Restore the top gap normally provided by the (now hidden) divider.
          isEmailUnverified && 'mt-6'
        )}
      >
        {/* Google sign-up is hidden in the unverified-email flow — the user
            must register with the email they typed in. */}
        {showGoogleSignUp ? (
          <GoogleSignUpButton deepLink={getGoogleReturnDeeplink()} />
        ) : null}

        <p className="text-magical-moonlight-dark text-sm text-center">
          Already have an account?{' '}
          <Button
            variant="link"
            onClick={() => void onBackToLogin()}
            className="text-blue-chaos underline hover:text-blue-chaos-dark p-0"
          >
            Login
          </Button>
        </p>
      </div>

      {/* Footer */}
      <FormFooter />
    </div>
  );
}
